import { useCallback, useState } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CardQuestion, CardActions } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import type { CardDef, CardOption } from "../types";

interface SingleChoiceCardProps {
  card: CardDef;
  value: string;
  onValue: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  index: number;
  error?: string;
}

export function SingleChoiceCard({
  card,
  value,
  onValue,
  onNext,
  onBack,
  onSkip,
  index,
  error,
}: SingleChoiceCardProps) {
  const { i18n } = useLingui();
  const [otherText, setOtherText] = useState("");
  const [touched, setTouched] = useState(false);

  const options: CardOption[] = card.options ?? [];
  const showOther = value === "other";
  const showError = touched && error;

  const handleSelect = (optValue: string) => {
    onValue(optValue);
    if (optValue !== "other" && card.autoAdvance) {
      setTimeout(() => onNext(), 200);
    }
  };

  const handleOk = useCallback(() => {
    setTouched(true);
    if (showOther && !otherText.trim()) return;
    onNext();
  }, [showOther, otherText, onNext]);

  useIntakeMobileChromeRegistration(
    () => {
      if (showOther || !card.autoAdvance) {
        return {
          onContinue: handleOk,
          onCancel: onBack,
          continueDisabled: showOther && !otherText.trim(),
          onSkip,
        };
      }
      return {
        onCancel: onBack,
        showContinue: false,
        onSkip,
      };
    },
    [showOther, card.autoAdvance, card.id, handleOk, onBack, onSkip, otherText],
  );

  return (
    <div>
      <CardQuestion question={card.question} hint={card.hint} index={index} />

      {showError && <p className="mb-4 text-sm text-red-500">{error}</p>}

      <div className="flex flex-col gap-1.5 sm:gap-2 max-w-lg [&_button]:!m-0">
        {options.map((opt, i) => {
          const isSelected = value === opt.value;
          const letter = String.fromCharCode(65 + i); // A, B, C…
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelect(opt.value)}
              className={`choice-pill flex items-center gap-2.5 sm:gap-3 px-4 py-2.5 sm:py-3 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? "border-[#166B93] bg-[#166B93]/8 text-[#166B93]"
                  : "border-gray-200 bg-white text-gray-700 hover:border-[#166B93]/40"
              }`}
            >
              <span
                className={`flex-shrink-0 w-7 h-7 rounded-md border flex items-center justify-center text-xs font-bold transition-all ${
                  isSelected
                    ? "border-[#166B93] bg-[#166B93] text-white"
                    : "border-gray-300 text-gray-400"
                }`}
              >
                {letter}
              </span>
              <span className="font-medium text-sm">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* Inline "other" free-text */}
      {showOther && (
        <div className="mt-4 max-w-lg">
          <input
            type="text"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleOk()}
            placeholder={i18n._(msg`Please describe…`)}
            autoFocus
            className="w-full bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-5 py-3.5 text-[17px] text-gray-900 placeholder-gray-400 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-0 focus:border-[#166B93] focus:shadow-[0_4px_20px_rgba(22,107,147,0.08)]"
          />
        </div>
      )}

      {/* Show Continue + Back when "other" is picked (auto-advance handles the rest) */}
      {(showOther || !card.autoAdvance) && (
        <CardActions onNext={handleOk} onBack={onBack} onSkip={onSkip} />
      )}

      {!showOther && card.autoAdvance && (
        <CardActions
          onNext={() => {}}
          onBack={onBack}
          onSkip={onSkip}
          showContinue={false}
        />
      )}
    </div>
  );
}
